import {
  neutral,
  semantic,
  gradients,
  surfaceTokens,
  FONT_DISPLAY,
  EASE,
  EASE_OUT,
  RADII
} from './tokens';

/**
 * Surcharges globales des composants MUI — le cœur de la direction
 * artistique « Nova ».
 *
 * Règles de la DA appliquées ici :
 *  - un seul accent (bleu → indigo), utilisé pour l'action et la sélection ;
 *  - la profondeur vient d'un filet + d'une ombre diffuse, jamais d'un
 *    contour dur ; l'ombre colorée est réservée au bouton primaire ;
 *  - les tableaux sont la surface principale du CRM : en-tête discret en
 *    petites capitales, lignes aérées, sélection marquée par un rail
 *    d'accent à gauche plutôt que par un aplat criard ;
 *  - toutes les transitions partagent la même courbe.
 *
 * Contrainte technique : objets simples uniquement (jamais de callbacks
 * `({ theme }) => …`), car le projet mélange @material-ui/core 5-beta et
 * @mui/material 5 et seule cette forme est interprétée par les deux.
 */
const buildComponents = (mode) => {
  const isLight = mode === 'light';
  const {
    canvas,
    line,
    lineStrong,
    textPrimary,
    textSecondary,
    hover,
    headTint,
    focusRing,
    focusColor,
    overlayShadow
  } = surfaceTokens(isLight);

  const fieldBg = isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.03)';
  const fieldBgHover = isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.05)';
  const softChip = isLight ? 'rgba(15, 23, 42, 0.04)' : 'rgba(255, 255, 255, 0.06)';
  const selectedTint = isLight ? 'rgba(59, 130, 246, 0.07)' : 'rgba(96, 165, 250, 0.12)';
  const selectedTintHover = isLight ? 'rgba(59, 130, 246, 0.11)' : 'rgba(96, 165, 250, 0.18)';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box'
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility',
          height: '100%',
          width: '100%'
        },
        body: {
          height: '100%',
          backgroundColor: canvas
        },
        '#root': {
          height: '100%'
        },
        '#nprogress .bar': {
          zIndex: '2000 !important',
          background: gradients.aurora,
          height: '3px'
        },
        // Chiffres alignés : indispensable dans les tableaux et les KPI.
        '.MuiTableCell-root, .tabular-nums': {
          fontVariantNumeric: 'tabular-nums'
        },
        '::selection': {
          backgroundColor: isLight ? 'rgba(59, 130, 246, 0.18)' : 'rgba(96, 165, 250, 0.3)'
        },
        '::-webkit-scrollbar': {
          width: 10,
          height: 10
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: isLight ? 'rgba(100, 116, 139, 0.28)' : 'rgba(148, 163, 184, 0.28)',
          borderRadius: 999,
          border: '2px solid transparent',
          backgroundClip: 'content-box'
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: isLight ? 'rgba(100, 116, 139, 0.45)' : 'rgba(148, 163, 184, 0.45)'
        }
      }
    },

    /* ---------------------------------------------------- Surfaces -- */
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        rounded: {
          borderRadius: RADII.card
        },
        outlined: {
          borderColor: line
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: RADII.card,
          border: `1px solid ${line}`,
          boxShadow: isLight
            ? '0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 28px -20px rgba(15, 23, 42, 0.22)'
            : '0 1px 2px rgba(0, 0, 0, 0.4), 0 10px 28px -20px rgba(0, 0, 0, 0.75)',
          transition: `box-shadow 0.25s ${EASE}, border-color 0.25s ${EASE}, transform 0.25s ${EASE}`
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: 'h6' },
        subheaderTypographyProps: { variant: 'body2' }
      },
      styleOverrides: {
        root: {
          padding: '20px 22px 12px'
        },
        title: {
          letterSpacing: '-0.012em'
        },
        subheader: {
          marginTop: 2,
          color: textSecondary
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '12px 22px 18px',
          gap: 8
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: `${RADII.card}px !important`,
          border: `1px solid ${line}`,
          boxShadow: 'none',
          overflow: 'hidden',
          '&:before': {
            display: 'none'
          }
        }
      }
    },

    /* ----------------------------------------------------- Actions -- */
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: RADII.control,
          fontWeight: 600,
          letterSpacing: '0.005em',
          padding: '8px 18px',
          position: 'relative',
          transition: `background 0.2s ${EASE}, background-color 0.2s ${EASE}, box-shadow 0.22s ${EASE}, transform 0.22s ${EASE_OUT}, border-color 0.2s ${EASE}, color 0.2s ${EASE}`,
          '&:active': {
            transform: 'translateY(0) scale(0.985)'
          },
          '&.Mui-disabled': {
            opacity: 0.5
          }
        },
        sizeSmall: {
          padding: '5px 13px',
          fontSize: '0.8125rem',
          borderRadius: 9
        },
        sizeLarge: {
          padding: '11px 24px',
          fontSize: '0.95rem',
          borderRadius: 12
        },
        // Bouton primaire : dégradé de marque + ombre teintée. C'est le
        // seul élément de l'interface qui a le droit à une ombre colorée.
        containedPrimary: {
          background: isLight ? gradients.primaryButton : undefined,
          boxShadow: isLight
            ? '0 1px 2px rgba(15, 23, 42, 0.16), 0 8px 18px -8px rgba(27, 36, 69, 0.55)'
            : '0 1px 2px rgba(0, 0, 0, 0.5)',
          '&:hover': {
            background: isLight ? gradients.primaryButtonHover : undefined,
            boxShadow: isLight
              ? '0 2px 4px rgba(15, 23, 42, 0.18), 0 14px 26px -10px rgba(27, 36, 69, 0.6)'
              : '0 8px 20px -8px rgba(0, 0, 0, 0.8)',
            transform: 'translateY(-1px)'
          }
        },
        containedSecondary: {
          background: isLight ? gradients.accent : undefined,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.14), 0 8px 18px -8px rgba(59, 130, 246, 0.5)',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(15, 23, 42, 0.16), 0 14px 26px -10px rgba(59, 130, 246, 0.55)',
            transform: 'translateY(-1px)'
          }
        },
        contained: {
          boxShadow: isLight
            ? '0 1px 2px rgba(15, 23, 42, 0.12), 0 6px 14px -8px rgba(15, 23, 42, 0.4)'
            : '0 1px 2px rgba(0, 0, 0, 0.4)',
          '&:hover': {
            transform: 'translateY(-1px)'
          },
          '&.Mui-disabled': {
            boxShadow: 'none'
          }
        },
        outlined: {
          backgroundColor: fieldBg,
          borderColor: lineStrong,
          '&:hover': {
            borderColor: focusColor,
            backgroundColor: isLight ? 'rgba(59, 130, 246, 0.05)' : 'rgba(96, 165, 250, 0.09)',
            transform: 'translateY(-1px)'
          }
        },
        text: {
          '&:hover': {
            backgroundColor: hover
          }
        }
      }
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: RADII.control
        },
        grouped: {
          borderColor: lineStrong
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: RADII.control,
          transition: `background-color 0.2s ${EASE}, color 0.2s ${EASE}, transform 0.2s ${EASE_OUT}`,
          '&:hover': {
            backgroundColor: softChip,
            transform: 'translateY(-1px)'
          },
          '&:active': {
            transform: 'scale(0.94)'
          }
        },
        sizeSmall: {
          borderRadius: 9
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0 10px 28px -10px rgba(27, 36, 69, 0.6)',
          textTransform: 'none',
          fontWeight: 600
        }
      }
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: isLight ? neutral[100] : 'rgba(255, 255, 255, 0.05)',
          borderRadius: 12,
          padding: 3,
          gap: 3
        },
        grouped: {
          border: '0 !important',
          borderRadius: '9px !important'
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.83rem',
          borderRadius: 9,
          borderColor: line,
          color: textSecondary,
          transition: `all 0.2s ${EASE}`,
          '&.Mui-selected': {
            backgroundColor: isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.1)',
            color: isLight ? textPrimary : '#ffffff',
            boxShadow: isLight ? '0 1px 2px rgba(15, 23, 42, 0.1), 0 4px 10px -6px rgba(15, 23, 42, 0.25)' : 'none'
          }
        }
      }
    },

    /* -------------------------------------------------- Formulaires -- */
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem'
        },
        input: {
          '&::placeholder': {
            opacity: 1,
            color: textSecondary
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADII.control,
          backgroundColor: fieldBg,
          transition: `box-shadow 0.2s ${EASE}, border-color 0.2s ${EASE}, background-color 0.2s ${EASE}`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: lineStrong,
            transition: `border-color 0.2s ${EASE}`
          },
          '&:hover': {
            backgroundColor: fieldBgHover
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isLight ? neutral[400] : 'rgba(148, 163, 184, 0.45)'
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 4px ${focusRing}`
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
            borderColor: focusColor
          },
          '&.Mui-disabled': {
            backgroundColor: isLight ? neutral[50] : 'rgba(255, 255, 255, 0.02)'
          }
        },
        inputSizeSmall: {
          paddingTop: 9,
          paddingBottom: 9
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: RADII.control,
          backgroundColor: isLight ? neutral[100] : 'rgba(255, 255, 255, 0.05)',
          '&:before, &:after': {
            display: 'none'
          }
        }
      }
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: textSecondary,
          '& .MuiSvgIcon-root': {
            fontSize: '1.15rem'
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
          '&.Mui-focused': {
            color: focusColor
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 2,
          fontSize: '0.75rem'
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.875rem'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: isLight ? neutral[400] : 'rgba(148, 163, 184, 0.6)',
          '&.Mui-checked': {
            color: focusColor
          },
          '&.MuiCheckbox-indeterminate': {
            color: focusColor
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: isLight ? neutral[400] : 'rgba(148, 163, 184, 0.6)',
          '&.Mui-checked': {
            color: focusColor
          }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          borderRadius: 999,
          opacity: isLight ? 0.3 : 0.38
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: textSecondary
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: RADII.card,
          border: `1px solid ${line}`,
          boxShadow: overlayShadow
        },
        option: {
          borderRadius: 8,
          margin: '2px 6px',
          fontSize: '0.875rem'
        },
        tag: {
          borderRadius: 8,
          fontWeight: 600
        }
      }
    },

    /* ------------------------------------------------------ Données -- */
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: RADII.card
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: headTint,
            backgroundImage: isLight
              ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(247, 249, 252, 0.9) 100%)'
              : 'none',
            color: textSecondary,
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            lineHeight: 1.7,
            paddingTop: 12,
            paddingBottom: 12,
            whiteSpace: 'nowrap',
            // En-tête collant : sur les longues listes du CRM, on garde
            // toujours le nom des colonnes sous les yeux.
            position: 'sticky',
            top: 0,
            zIndex: 2,
            // Filet dessiné en `box-shadow` : une `border-bottom` sur une
            // cellule collante disparaît au défilement en `border-collapse`.
            borderBottom: 'none',
            boxShadow: `inset 0 -1px 0 ${line}`
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${line}`,
          fontSize: '0.875rem',
          paddingTop: 12,
          paddingBottom: 12
        },
        sizeSmall: {
          paddingTop: 8,
          paddingBottom: 8
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color 0.15s ${EASE}`,
          // Survol / sélection : un rail d'accent apparaît à gauche de la
          // ligne. Plus lisible qu'un simple aplat quand les lignes sont
          // denses. Le rail est porté par la première cellule et non par
          // la ligne : une `box-shadow` sur un <tr> n'est pas peinte quand
          // le tableau est en `border-collapse: collapse`.
          '&.MuiTableRow-hover:hover': {
            backgroundColor: hover
          },
          '&.MuiTableRow-hover:hover > .MuiTableCell-root:first-of-type': {
            boxShadow: `inset 3px 0 0 ${focusColor}`
          },
          '&.Mui-selected': {
            backgroundColor: selectedTint
          },
          '&.Mui-selected > .MuiTableCell-root:first-of-type': {
            boxShadow: `inset 3px 0 0 ${focusColor}`
          },
          '&.Mui-selected:hover': {
            backgroundColor: selectedTintHover
          }
        }
      }
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'inherit',
          '&:hover': {
            color: focusColor
          },
          '&.Mui-active': {
            color: focusColor
          },
          '&.Mui-active .MuiTableSortLabel-icon': {
            color: focusColor
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${line}`,
          fontSize: '0.8125rem'
        },
        toolbar: {
          minHeight: 56
        },
        selectLabel: { fontSize: '0.8125rem' },
        displayedRows: { fontSize: '0.8125rem', fontWeight: 600 },
        select: {
          borderRadius: 8
        }
      }
    },
    // @mui/x-data-grid v5 ne résout que quelques slots (root, cell,
    // columnHeader, row…) : `columnHeaders` au pluriel est ignoré. Tout
    // passe donc par `root` avec des sélecteurs descendants — c'est ce qui
    // permet aux pages de ne plus porter aucun style de grille.
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${line}`,
          borderRadius: RADII.card,
          fontFamily: 'inherit',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: headTint,
            borderBottom: `1px solid ${line}`,
            borderRadius: 0
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: textSecondary
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${line}`
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none'
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none'
          },
          '& .MuiDataGrid-row': {
            transition: `background-color 0.15s ${EASE}, box-shadow 0.15s ${EASE}`
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: hover,
            boxShadow: `inset 3px 0 0 ${focusColor}`
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: selectedTint,
            boxShadow: `inset 3px 0 0 ${focusColor}`
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: selectedTintHover
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: 'transparent'
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${line}`,
            backgroundColor: headTint
          },
          '& .MuiDataGrid-selectedRowCount': {
            color: focusColor,
            fontWeight: 600
          },
          '& .MuiTablePagination-root': {
            color: textSecondary,
            borderTop: 0
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontWeight: 600,
            fontSize: '0.78rem'
          },
          // Barre d'outils (Colonnes / Filtres / Densité / Exporter)
          '& .MuiDataGrid-toolbarContainer': {
            gap: 4,
            padding: '10px 12px',
            borderBottom: `1px solid ${line}`
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
            fontSize: '0.78rem',
            fontWeight: 600,
            color: textSecondary,
            padding: '5px 10px'
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-root:hover': {
            backgroundColor: hover,
            color: focusColor
          },
          '& .MuiDataGrid-overlay': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.01em',
          transition: `all 0.2s ${EASE}`
        },
        sizeSmall: {
          borderRadius: 7,
          height: 24
        },
        outlined: {
          borderColor: lineStrong
        },
        deleteIcon: {
          fontSize: '1rem'
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.9rem'
        },
        fallback: {
          height: '75%',
          width: '75%'
        }
      }
    },
    MuiAvatarGroup: {
      styleOverrides: {
        avatar: {
          border: `2px solid ${isLight ? '#ffffff' : '#161e2d'}`,
          fontSize: '0.75rem'
        }
      }
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 700,
          fontSize: '0.66rem',
          minWidth: 18,
          height: 18,
          padding: '0 5px'
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          overflow: 'hidden',
          backgroundColor: isLight ? neutral[200] : 'rgba(255, 255, 255, 0.08)'
        },
        bar: {
          borderRadius: 999
        }
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: isLight ? 'rgba(15, 23, 42, 0.07)' : 'rgba(255, 255, 255, 0.07)'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: line
        }
      }
    },

    /* --------------------------------------------------- Navigation -- */
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 46
        },
        // Indicateur « aurora » : la signature du produit se retrouve sur
        // chaque onglet actif.
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          background: gradients.aurora
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.885rem',
          minHeight: 46,
          letterSpacing: '0.005em',
          borderRadius: '10px 10px 0 0',
          color: textSecondary,
          transition: `color 0.2s ${EASE}, background-color 0.2s ${EASE}`,
          '&:hover': {
            backgroundColor: hover,
            color: textPrimary
          },
          '&.Mui-selected': {
            color: isLight ? textPrimary : '#ffffff'
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '14px',
          color: textSecondary
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: RADII.control,
          transition: `background-color 0.15s ${EASE}`
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.82rem'
        },
        separator: {
          color: textSecondary
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textUnderlineOffset: '3px',
          transition: `color 0.2s ${EASE}`
        }
      }
    },

    /* ----------------------------------------------------- Overlays -- */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: RADII.overlay,
          backgroundImage: 'none',
          boxShadow: isLight
            ? '0 32px 64px -24px rgba(15, 23, 42, 0.35)'
            : '0 32px 64px -24px rgba(0, 0, 0, 0.8)'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: FONT_DISPLAY,
          fontSize: '1.15rem',
          fontWeight: 700,
          letterSpacing: '-0.014em'
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: 8
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: isLight ? 'rgba(15, 23, 42, 0.45)' : 'rgba(2, 6, 16, 0.65)',
          backdropFilter: 'blur(3px)'
        },
        invisible: {
          backgroundColor: 'transparent',
          backdropFilter: 'none'
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: RADII.card,
          border: `1px solid ${line}`,
          boxShadow: overlayShadow,
          backgroundImage: 'none'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: RADII.card,
          border: `1px solid ${line}`,
          boxShadow: overlayShadow,
          backgroundImage: 'none'
        },
        list: {
          padding: 6
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          fontSize: '0.875rem',
          minHeight: 38,
          transition: `background-color 0.15s ${EASE}`,
          '&.Mui-selected': {
            backgroundColor: selectedTint
          },
          '&.Mui-selected:hover': {
            backgroundColor: selectedTintHover
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isLight ? neutral[900] : '#0b1220',
          color: '#ffffff',
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '7px 11px',
          boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.45)'
        },
        arrow: {
          color: isLight ? neutral[900] : '#0b1220'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: '0.875rem',
          alignItems: 'center'
        },
        standardSuccess: { backgroundColor: isLight ? semantic.success.soft : 'rgba(18, 183, 106, 0.14)' },
        standardWarning: { backgroundColor: isLight ? semantic.warning.soft : 'rgba(247, 144, 9, 0.14)' },
        standardError: { backgroundColor: isLight ? semantic.error.soft : 'rgba(240, 68, 56, 0.14)' },
        standardInfo: { backgroundColor: isLight ? semantic.info.soft : 'rgba(11, 165, 236, 0.14)' }
      }
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 12
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    }
  };
};

export default buildComponents;
